import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { LoadableState } from '../../../../shared/types/LoadableState';
import { PARTNER_STORE } from '../../domain/repo/store/PartnerStore';
import { AttachVehicleFormModel } from '../../domain/repo/types/AttachVehicleFormModel';
import { PartnerCity } from '../../domain/repo/types/PartnerCity';
import {
  PartnerFormInput,
  partnerFormSchema,
  toFormOutput,
} from '../../domain/repo/types/PartnerFormInterface';
import { PartnerVehicle } from '../../domain/repo/types/PartnerVehicle';
import { SelectOption } from '../../types';
import {
  infoSourceToSelectConv,
  partnerCityToSelectConv,
  partnerVehicleToSelectConv,
} from '../helpers/PartnersHelper';
import styles from '../styles/PartnersForm.module.scss';

interface PartnersFormCmpProps {
  readonly initialCity: PartnerCity;
  readonly submitPartnerDetailsInfo: (form: AttachVehicleFormModel) => void;
  readonly setCity: (city: string) => void;
  readonly cities: PartnerCity[];
  readonly vehicles: LoadableState<PartnerVehicle[]>;
  readonly infoSourceList: string[];
}

function PartnersFormCmp({
  initialCity,
  submitPartnerDetailsInfo,
  setCity,
  cities,
  vehicles,
  infoSourceList,
}: PartnersFormCmpProps): React.ReactElement {
  const citiesOptions: SelectOption[] = cities.map(partnerCityToSelectConv);
  const infoSourceOptions: SelectOption[] = infoSourceList.map(infoSourceToSelectConv);

  const initialCityOption = citiesOptions.find(
    (opt: SelectOption) => opt.key === String(initialCity.id),
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PartnerFormInput>({
    mode: 'onBlur',
    resolver: zodResolver(partnerFormSchema),
    defaultValues: {
      name: '',
      mobile: '',
      city: initialCityOption,
      vehicle: undefined,
      infoSource: undefined,
    },
  });

  const watchCity = watch('city');
  const watchVehicle = watch('vehicle');
  const watchInfoSource = watch('infoSource');

  const onSubmit = (data: PartnerFormInput): void => {
    const output = toFormOutput(data);
    submitPartnerDetailsInfo({
      name: output.name,
      mobile: output.mobile,
      city: output.city,
      vehicleType: output.vehicle,
      infoSource: output.infoSource,
    });
  };

  const getVehicleOptions = (): SelectOption[] => {
    if (vehicles.status === 'live') {
      return vehicles.data.map(partnerVehicleToSelectConv);
    }
    return [];
  };

  const getVehiclePlaceholder = (): string => {
    switch (vehicles.status) {
      case 'loading':
        return PARTNER_STORE.loading_vehicles;
      case 'error':
        return PARTNER_STORE.try_again;
      default:
        return PARTNER_STORE.vehicle_placeholder;
    }
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const selected = citiesOptions.find(
      (opt: SelectOption) => opt.key === e.target.value,
    );
    if (selected) {
      setValue('city', selected, { shouldValidate: true });
      setValue('vehicle', undefined as never);
      setCity(selected.value);
    }
  };

  const handleVehicleChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    const vehicleOptions = getVehicleOptions();
    const selected = vehicleOptions.find(
      (opt: SelectOption) => opt.key === e.target.value,
    );
    if (selected) {
      setValue('vehicle', selected, { shouldValidate: true });
    }
  };

  const handleSourceChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    const selected = infoSourceOptions.find(
      (opt: SelectOption) => opt.key === e.target.value,
    );
    if (selected) {
      setValue('infoSource', selected, { shouldValidate: true });
    }
  };

  return (
    <form
      className={styles['attach-vehicle-form']}
      id="partner-form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className={styles['modal-form-input']}>
        <label>Name</label>
        <input
          {...register('name')}
          placeholder={PARTNER_STORE.name_placeholder}
        />
        <span className={styles['error-text']}>{errors.name?.message}</span>
      </div>

      <div className={styles['modal-form-input']}>
        <label>Mobile Number</label>
        <div className={styles['mobile-input-wrapper']}>
          <span className={styles['isd-code']}>{PARTNER_STORE.isd_code} -</span>
          <input
            {...register('mobile')}
            placeholder={PARTNER_STORE.mobile_placeholder}
          />
        </div>
        <span className={styles['error-text']}>{errors.mobile?.message}</span>
      </div>

      <div className={styles['same-row-fields']}>
        <div className={styles['modal-form-input']}>
          <label>City</label>
          <select value={watchCity?.key ?? ''} onChange={handleCityChange}>
            <option value="">{PARTNER_STORE.city_placeholder}</option>
            {citiesOptions.map((opt: SelectOption) => (
              <option key={opt.key} value={opt.key}>
                {opt.text}
              </option>
            ))}
          </select>
          <span className={styles['error-text']}>{errors.city?.message}</span>
        </div>

        <div className={styles['modal-form-input']}>
          <label>Vehicle</label>
          {vehicles.status === 'live' ? (
            <select
              value={watchVehicle?.key ?? ''}
              onChange={handleVehicleChange}
            >
              <option value="">{PARTNER_STORE.vehicle_placeholder_caps}</option>
              {getVehicleOptions().map((opt: SelectOption) => (
                <option key={opt.key} value={opt.key}>
                  {opt.text}
                </option>
              ))}
            </select>
          ) : (
            <input disabled placeholder={getVehiclePlaceholder()} />
          )}
          <span className={styles['error-text']}>{errors.vehicle?.message}</span>
        </div>
      </div>

      <div className={styles['modal-form-input']}>
        <label>Source</label>
        <select
          value={watchInfoSource?.key ?? ''}
          onChange={handleSourceChange}
        >
          <option value="">{PARTNER_STORE.source_placeholder}</option>
          {infoSourceOptions.map((opt: SelectOption) => (
            <option key={opt.key} value={opt.key}>
              {opt.text}
            </option>
          ))}
        </select>
        <span className={styles['error-text']}>{errors.infoSource?.message}</span>
      </div>

      <button
        type="submit"
        className={`${styles['modal-form-button']} ${styles['request-btn']}`}
      >
        {PARTNER_STORE.register_btn}
      </button>
    </form>
  );
}

export default PartnersFormCmp;
